import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AddchartOutlinedIcon from '@mui/icons-material/AddchartOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import { Button, CircularProgress, IconButton, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useSubmitReportingLeDataFeedback } from '~/hooks/apis/reporting/useSubmitReportingLeDataFeedback';

import type { LeDataChatMessage, LeDataFeedbackRating } from '~/types/leData';

import { buildAnswerImageFilename, copyAnswerAsImage } from '~/utils/leData/answerImageExport';
import { getLeDataApiErrorMessage } from '~/utils/leData/getLeDataApiErrorMessage';

import FeedbackCommentPopover from '../chat/FeedbackCommentPopover';
import type { AnswerDoors } from '../chat/LeDataMessageBlocks';
import { SaveSqlToLensDialog } from '../chat/SqlCodeMirrorBlock';
import { formatAssistantMessageMarkdown } from '../chat/answerMarkdown';
import { ICON_PRESS_SX } from '../chat/uiTokens';
import type { AnswerSpeech } from '../chat/useAnswerSpeech';
import { useExportPdf } from '../chat/useExportPdf';

export const ACTION_BUTTON_SX = { width: 36, height: 36, color: 'inherit', ...ICON_PRESS_SX } as const;
const COPIED_TICK_MS = 1600;
const ANSWER_CARD_SELECTOR = '[data-testid="settled-answer-card"]';
const ANSWER_TURN_SELECTOR = '.ui-stream-turn';

interface AnswerActionsProps {
  work?: React.ReactNode;
  lensUndo?: React.ReactNode;
  speech?: AnswerSpeech;
  message: LeDataChatMessage;
  sessionId?: string;
  doors: AnswerDoors;
}

export default function AnswerActions({ message, sessionId, doors, work, lensUndo, speech }: AnswerActionsProps) {
  const theme = useTheme();
  const accent = lensTokens(theme).threadAccent;
  const { enqueueSnackbar } = useSnackbar();
  const { exporting, exportMessage } = useExportPdf();
  const { mutateAsync: submitFeedback, isPending: feedbackPending } = useSubmitReportingLeDataFeedback();
  const [rating, setRating] = useState<LeDataFeedbackRating | undefined>(message.feedbackRating);
  const [copied, setCopied] = useState(false);
  const [copyAnchor, setCopyAnchor] = useState<HTMLElement | null>(null);
  const [downAnchor, setDownAnchor] = useState<HTMLElement | null>(null);
  const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);
  const [addingToLens, setAddingToLens] = useState(false);
  const [copyingImage, setCopyingImage] = useState(false);

  const copyAnswer = async () => {
    const markdown = formatAssistantMessageMarkdown(message);
    if (!markdown) {
      enqueueSnackbar('Nothing to copy', { variant: 'warning' });
      return;
    }
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPIED_TICK_MS);
    } catch {
      enqueueSnackbar('Copying failed', { variant: 'warning' });
    }
  };

  const copyImage = async (button: HTMLElement) => {
    setCopyingImage(true);
    try {
      const result = await copyAnswerAsImage({
        node:
          button.closest(ANSWER_TURN_SELECTOR)?.querySelector<HTMLElement>(ANSWER_CARD_SELECTOR) ??
          button.closest<HTMLElement>(ANSWER_TURN_SELECTOR),
        backgroundColor: theme.palette.background.paper,
        filename: buildAnswerImageFilename(message.id),
      });
      if (result.status === 'copied') {
        enqueueSnackbar('Copied answer as image', { variant: 'success' });
      } else if (result.status === 'downloaded') {
        enqueueSnackbar('Clipboard unavailable, downloaded the image instead', { variant: 'info' });
      } else {
        enqueueSnackbar('Could not copy image', { variant: 'warning' });
      }
    } finally {
      setCopyingImage(false);
    }
  };

  const downloadAnswer = () => {
    setDownloadAnchor(null);
    void exportMessage(message, sessionId ?? null);
  };

  const downloadTable = () => {
    setDownloadAnchor(null);
    doors.downloadTable?.();
  };

  const rate = async (next: LeDataFeedbackRating, comment?: string) => {
    if (!sessionId) {
      return;
    }
    try {
      await submitFeedback({ sessionId, rating: next, traceId: message.traceId, comment });
      setRating(next);
      enqueueSnackbar('Thanks for the feedback', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getLeDataApiErrorMessage(error), { variant: 'error' });
    }
  };

  const ratingLocked = !sessionId || feedbackPending;

  const speaking = speech?.speakingId === message.id;
  const preparingSpeech = speech?.preparingId === message.id;
  const speechLabel = speaking || preparingSpeech ? 'Stop reading' : 'Read aloud';

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.25}
      data-testid="ui-stream-answer-actions"
      data-html2canvas-ignore
      sx={{
        mt: 0.5,
        ml: -0.75,
        flexWrap: 'wrap',
        rowGap: 0.5,
        color: 'text.secondary',
        opacity: 0.72,
        transition: 'opacity 150ms ease',
        '&:hover, &:focus-within': { opacity: 1 },
        '.ui-stream-turn:hover &': { opacity: 1 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Tooltip title={copied ? 'Copied' : 'Copy'}>
        <span>
          <IconButton
            size="small"
            aria-label="Copy"
            aria-haspopup="menu"
            aria-expanded={Boolean(copyAnchor)}
            disabled={copyingImage}
            onClick={(event) => setCopyAnchor(event.currentTarget)}
            sx={ACTION_BUTTON_SX}
          >
            {copyingImage ? (
              <CircularProgress size={16} />
            ) : copied ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ContentCopyOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={copyAnchor} open={Boolean(copyAnchor)} onClose={() => setCopyAnchor(null)}>
        <MenuItem
          aria-label="Copy answer"
          onClick={() => {
            setCopyAnchor(null);
            void copyAnswer();
          }}
        >
          Text
        </MenuItem>
        <MenuItem
          aria-label="Copy as image"
          disabled={copyingImage}
          onClick={() => {
            if (copyAnchor) {
              void copyImage(copyAnchor);
            }
            setCopyAnchor(null);
          }}
        >
          Image
        </MenuItem>
      </Menu>
      {speech ? (
        <Tooltip title={speechLabel}>
          <IconButton
            size="small"
            aria-label={speechLabel}
            aria-pressed={speaking}
            onClick={() => void speech.toggleSpeech(message.id, formatAssistantMessageMarkdown(message))}
            sx={ACTION_BUTTON_SX}
          >
            {preparingSpeech ? (
              <CircularProgress size={16} />
            ) : speaking ? (
              <StopCircleOutlinedIcon fontSize="small" />
            ) : (
              <VolumeUpOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title="Download">
        <span>
          <IconButton
            size="small"
            aria-label="Download"
            aria-haspopup={doors.downloadTable ? 'menu' : undefined}
            disabled={exporting}
            onClick={(event) => (doors.downloadTable ? setDownloadAnchor(event.currentTarget) : downloadAnswer())}
            sx={ACTION_BUTTON_SX}
          >
            {exporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={downloadAnchor} open={Boolean(downloadAnchor)} onClose={() => setDownloadAnchor(null)}>
        <MenuItem onClick={downloadTable}>Table as CSV</MenuItem>
        <MenuItem onClick={downloadAnswer}>Answer as PDF</MenuItem>
      </Menu>
      <Tooltip title="Good answer">
        <span>
          <IconButton
            size="small"
            aria-label="Good answer"
            aria-pressed={rating === 'up'}
            disabled={ratingLocked || rating === 'up'}
            onClick={() => void rate('up')}
            sx={ACTION_BUTTON_SX}
          >
            {rating === 'up' ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Poor answer">
        <span>
          <IconButton
            size="small"
            aria-label="Poor answer"
            aria-pressed={rating === 'down'}
            disabled={ratingLocked || rating === 'down'}
            onClick={(event) => setDownAnchor(event.currentTarget)}
            sx={ACTION_BUTTON_SX}
          >
            {rating === 'down' ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <FeedbackCommentPopover
        anchorEl={downAnchor}
        pending={feedbackPending}
        onClose={() => setDownAnchor(null)}
        onSubmit={(comment) => {
          setDownAnchor(null);
          void rate('down', comment);
        }}
      />
      {lensUndo}
      {work}
      {doors.addToLens?.preferredLensId ? (
        <Button
          size="small"
          startIcon={<AddchartOutlinedIcon />}
          onClick={() => setAddingToLens(true)}
          sx={{ color: accent }}
        >
          Add to this Lens
        </Button>
      ) : doors.buildLens ? (
        <Button size="small" startIcon={<AddchartOutlinedIcon />} onClick={doors.buildLens} sx={{ color: accent }}>
          Build Lens
        </Button>
      ) : null}
      {addingToLens && doors.addToLens ? (
        <SaveSqlToLensDialog {...doors.addToLens} targetLensOnly open onClose={() => setAddingToLens(false)} />
      ) : null}
    </Stack>
  );
}
